import * as THREE from 'three';

export type TargetKind = 'standard' | 'moving' | 'small' | 'armored' | 'golden' | 'drone';

export interface TargetConfig {
  kind: TargetKind;
  position: THREE.Vector3; // base position (ground anchor, pole bottom)
  scale: number;
  standHeight: number; // how high the disc rises when "up"
  lifetimeMs: number; // time fully up before auto-retreating
  driftSpeed: number; // horizontal drift, 0 = stationary
  driftRange: number;
  hitsRequired: number;
  basePointsMultiplier: number; // relative to weapon.basePoints
}

let cachedTextures: Partial<Record<TargetKind, THREE.CanvasTexture>> = {};

function buildBullseyeTexture(kind: TargetKind): THREE.CanvasTexture {
  const cached = cachedTextures[kind];
  if (cached) return cached;

  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  const palette: Record<TargetKind, string[]> = {
    standard: ['#f2f2f2', '#c0272d', '#f2f2f2', '#c0272d'],
    moving: ['#f2f2f2', '#c0272d', '#f2f2f2', '#c0272d'],
    small: ['#f2f2f2', '#c0272d', '#f2f2f2', '#c0272d'],
    armored: ['#dfe4e8', '#3a4a55', '#dfe4e8', '#1f2933'],
    golden: ['#fff3c4', '#e6a817', '#fff3c4', '#b8860b'],
    drone: ['#e0c9ff', '#6a2ec4', '#e0c9ff', '#3d1878'],
  };
  const rings = palette[kind];

  ctx.fillStyle = '#141014';
  ctx.fillRect(0, 0, size, size);

  const cx = size / 2;
  const cy = size / 2;
  const ringCount = rings.length;
  for (let i = ringCount; i > 0; i--) {
    ctx.beginPath();
    ctx.arc(cx, cy, (size / 2 - 6) * (i / ringCount), 0, Math.PI * 2);
    ctx.fillStyle = rings[ringCount - i];
    ctx.fill();
  }

  // Center dot
  ctx.beginPath();
  ctx.arc(cx, cy, size * 0.06, 0, Math.PI * 2);
  ctx.fillStyle = kind === 'golden' ? '#7a5200' : kind === 'drone' ? '#1a0a33' : '#1a1a1a';
  ctx.fill();

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  cachedTextures[kind] = tex;
  return tex;
}

export class Target {
  group: THREE.Group;
  config: TargetConfig;
  hitsRemaining: number;
  state: 'rising' | 'up' | 'falling' | 'dead' = 'rising';
  private timeUp = 0;
  private disc: THREE.Mesh;
  private pole: THREE.Mesh;
  private driftDir = 1;
  private driftOffset = 0;
  private baseX: number;

  constructor(config: TargetConfig) {
    this.config = config;
    this.hitsRemaining = config.hitsRequired;
    this.baseX = config.position.x;

    this.group = new THREE.Group();
    this.group.position.copy(config.position);
    this.group.position.y = -config.standHeight; // start hidden below ground/cover
    this.group.scale.setScalar(config.scale);

    const poleGeo = new THREE.CylinderGeometry(0.04, 0.05, config.standHeight * 1.3, 5);
    const poleMat = new THREE.MeshLambertMaterial({ color: 0x5a4632 });
    this.pole = new THREE.Mesh(poleGeo, poleMat);
    this.pole.position.y = config.standHeight * 0.5;
    this.group.add(this.pole);

    const discGeo = new THREE.CircleGeometry(0.42, 16);
    const texture = buildBullseyeTexture(config.kind);
    const discMat = new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide });
    this.disc = new THREE.Mesh(discGeo, discMat);
    this.disc.position.y = config.standHeight * 1.05;
    this.group.add(this.disc);

    // Chunky low-poly backing block behind the disc (authentic PS1 "flat prop"
    // silhouette rather than a paper-thin cutout).
    const backGeo = new THREE.BoxGeometry(0.85, 0.85, 0.08);
    const backMat = new THREE.MeshLambertMaterial({
      color: config.kind === 'golden' ? 0x8a6d1f : config.kind === 'drone' ? 0x352060 : 0x3a3226,
    });
    const back = new THREE.Mesh(backGeo, backMat);
    back.position.y = config.standHeight * 1.05;
    back.position.z = -0.05;
    this.group.add(back);
  }

  // Returns true while the target should still be considered "alive" (not
  // yet fully retreated / removed).
  update(dtMs: number): boolean {
    if (this.state === 'rising') {
      this.group.position.y += dtMs * 0.006;
      if (this.group.position.y >= 0) {
        this.group.position.y = 0;
        this.state = 'up';
        this.timeUp = 0;
      }
    } else if (this.state === 'up') {
      this.timeUp += dtMs;
      if (this.config.driftSpeed > 0) {
        this.driftOffset += (this.config.driftSpeed * dtMs) / 1000;
        if (Math.abs(this.driftOffset) > this.config.driftRange) {
          this.driftDir *= -1;
        }
        this.group.position.x = this.baseX + this.driftOffset * this.driftDir;
      }
      if (this.timeUp >= this.config.lifetimeMs) {
        this.state = 'falling';
      }
    } else if (this.state === 'falling') {
      this.group.position.y -= dtMs * 0.008;
      if (this.group.position.y <= -this.config.standHeight) {
        this.state = 'dead';
      }
    }
    return this.state !== 'dead';
  }

  // Called when a raycast hit lands on this target. Returns true if the
  // target is fully destroyed (hitsRemaining reached zero).
  registerHit(): boolean {
    this.hitsRemaining -= 1;
    if (this.hitsRemaining <= 0) {
      this.state = 'falling';
      return true;
    }
    return false;
  }

  get worldTopPosition(): THREE.Vector3 {
    const v = new THREE.Vector3();
    this.disc.getWorldPosition(v);
    return v;
  }

  dispose(scene: THREE.Scene) {
    scene.remove(this.group);
  }
}
